import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ShieldCheck, ArrowRight } from "lucide-react";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";
import { STATES, CITIES, CITIES_PENDING_VERIFICATION } from "../data/nationalArchitecture";

export default function LocationsPage() {
  useSEO({
    title: "NDIS Locations Australia | Alpha Community Care | State & City Directory",
    description: "Alpha Community Care services participants Australia-wide, with active operational hubs in Melbourne (Victoria) and Perth (Western Australia). Explore state and city NDIS support directory.",
    keywords: [
      "NDIS locations Australia", "NDIS provider directory", "NDIS Melbourne", "NDIS Perth",
      "NDIS Sydney", "NDIS Brisbane", "NDIS Adelaide", "NDIS Hobart", "NDIS Canberra", "NDIS Darwin",
    ],
    canonical: `${SITE_URL}/locations`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }])],
  });

  const hubStates = Object.values(STATES).filter((s) => s.capability === "hub");
  const servicedStates = Object.values(STATES).filter((s) => s.capability !== "hub");

  return (
    <div data-testid="page-locations">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Locations directory</div>
          <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">Where Alpha operates — across Australia.</h1>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">Active operational hubs in Melbourne (Victoria) and Perth (Western Australia). National intake for every other state and territory — assessed case-by-case, with honest capacity feedback.</p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <a href="tel:1300380692" className="alpha-btn-primary !py-2 !px-4"><Phone className="w-4 h-4" /> 1300 380 692</a>
            <Link to="/ndis-provider-australia" className="alpha-btn-green !py-2 !px-4">National overview <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Active hubs */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="alpha-overline alpha-overline-green">Active operational hubs</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium">Where Alpha delivers on the ground.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {hubStates.map((s) => {
              const stateCities = Object.values(CITIES).filter((c) => c.stateSlug === s.slug);
              return (
                <div key={s.slug} className="alpha-card p-8" data-testid={`loc-hub-${s.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl alpha-gradient text-white flex items-center justify-center font-display text-lg font-semibold">{s.code}</div>
                    <div>
                      <Link to={`/locations/${s.slug}`} className="font-display text-2xl font-semibold hover:text-[color:var(--alpha-purple-700)]">{s.name}</Link>
                      <div className="text-xs text-[color:var(--alpha-green-700)] font-semibold mt-0.5 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Active operational hub
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[color:var(--alpha-mute)]">{s.tagline}.</p>
                  {stateCities.length > 0 && (
                    <div className="mt-5">
                      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[color:var(--alpha-mute)] mb-2">Cities</div>
                      <div className="grid grid-cols-2 gap-2">
                        {stateCities.map((c) => (
                          <Link key={c.slug} to={`/locations/${s.slug}/${c.slug}`} className="text-sm inline-flex items-center gap-1 text-[color:var(--alpha-purple-700)] hover:underline" data-testid={`loc-hub-${s.slug}-${c.slug}`}>
                            <MapPin className="w-3 h-3" /> {c.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  <Link to={`/locations/${s.slug}`} className="mt-6 alpha-btn-ghost text-sm inline-flex" data-testid={`loc-hub-${s.slug}-more`}>
                    Explore {s.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Serviced states */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="alpha-overline">National service capability</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium">Serviced through national intake.</h2>
          <p className="mt-3 text-[color:var(--alpha-mute)] max-w-3xl">Every state below is supported by our national intake team. We assess capacity, clinical scope and workforce availability before we commit — and we say honestly when we're not the right provider.</p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {servicedStates.map((s) => {
              const stateCities = Object.values(CITIES).filter((c) => c.stateSlug === s.slug);
              return (
                <div key={s.slug} className="alpha-card p-6" data-testid={`loc-serviced-${s.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-100)] text-[color:var(--alpha-purple-700)] flex items-center justify-center font-display font-semibold">{s.code}</div>
                    <div>
                      <Link to={`/locations/${s.slug}`} className="font-display text-lg font-semibold hover:text-[color:var(--alpha-purple-700)]">{s.name}</Link>
                    </div>
                  </div>
                  {stateCities.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {stateCities.map((c) => (
                        <Link key={c.slug} to={`/locations/${s.slug}/${c.slug}`} className="text-xs px-2.5 py-1 rounded-full bg-white border border-[color:var(--alpha-line)] hover:border-[color:var(--alpha-purple-700)] hover:text-[color:var(--alpha-purple-700)]" data-testid={`loc-serviced-${s.slug}-${c.slug}`}>
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  )}
                  <Link to={`/locations/${s.slug}`} className="mt-5 text-sm font-semibold text-[color:var(--alpha-purple-700)] hover:underline inline-flex items-center gap-1" data-testid={`loc-serviced-${s.slug}-more`}>
                    Explore {s.name} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pending management verification note */}
      {CITIES_PENDING_VERIFICATION.length > 0 && (
        <section className="alpha-section">
          <div className="alpha-container max-w-3xl">
            <div className="alpha-overline">Additional service enquiries</div>
            <h2 className="mt-3 font-display text-2xl font-medium">Somewhere else in Australia?</h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] leading-relaxed">
              Alpha's national intake also assesses enquiries from regional and secondary cities including
              {" "}{CITIES_PENDING_VERIFICATION.map((c) => c.name).join(", ")}. Local service capability is confirmed case-by-case — call{" "}
              <a href="tel:1300380692" className="font-semibold text-[color:var(--alpha-purple-700)]">1300 380 692</a> or{" "}
              <a href="mailto:hello@alphacommunitycare.com.au" className="font-semibold text-[color:var(--alpha-purple-700)]">email intake</a>.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
